import { useState, useEffect } from 'react';
import { Smartphone, X, Share, Plus } from 'lucide-react';
import { usePwaInstallPrompt } from '../../hooks/usePwaInstallPrompt';

/**
 * Install DocSite App banner — appears at bottom of the Doctor Dashboard
 * on mobile devices when the PWA is not yet installed. Tapping the install
 * button fires the native browser install dialog (Android/Chrome/Edge).
 * On iOS Safari we show a 2-step "Tap Share → Add to Home Screen" guide
 * since iOS has no programmatic install API.
 *
 * Dismissal is remembered for 14 days so we never nag.
 */
const DISMISS_KEY = 'docsite_install_banner_dismissed_until';

export default function InstallAppBanner() {
  const { canInstall, promptInstall, isIOS, isAndroid, isMobile, isInstalled } = usePwaInstallPrompt();
  const [dismissed, setDismissed] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (until && Date.now() < until) setDismissed(true);
  }, []);

  if (dismissed || isInstalled || !isMobile) return null;
  // On Android/Chrome we need a captured prompt; on iOS we always show the guide.
  if (!canInstall && !isIOS) return null;

  const handleInstall = async () => {
    if (isIOS) {
      setShowIosGuide(true);
      return;
    }
    const outcome = await promptInstall();
    if (outcome === 'dismissed') handleDismiss();
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 14 * 24 * 3600 * 1000));
    setDismissed(true);
  };

  return (
    <>
      <div
        className="fixed bottom-3 left-3 right-3 z-40 sm:hidden bg-gradient-to-r from-indigo-600 via-sky-600 to-emerald-500 text-white rounded-2xl shadow-2xl p-3.5 flex items-center gap-3"
        data-testid="install-app-banner"
      >
        <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
          <Smartphone size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Install DocSite App</p>
          <p className="text-[11px] text-white/85 leading-tight">Faster open, offline access, push login codes — no app store needed.</p>
        </div>
        <button
          onClick={handleInstall}
          className="shrink-0 rounded-full px-3.5 py-1.5 bg-white text-indigo-700 text-xs font-bold hover:bg-indigo-50"
          data-testid="install-app-btn"
        >
          {isIOS ? 'How' : 'Install'}
        </button>
        <button onClick={handleDismiss} className="shrink-0 text-white/80 hover:text-white p-1" data-testid="install-app-dismiss" aria-label="Dismiss">
          <X size={16} />
        </button>
      </div>

      {showIosGuide && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setShowIosGuide(false)} data-testid="ios-install-guide">
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Install DocSite on iPhone</p>
              <button onClick={() => setShowIosGuide(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <ol className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center shrink-0">1</span>
                <span>Tap the <Share size={14} className="inline mx-0.5 text-sky-600" /> <b>Share</b> button at the bottom of Safari.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center shrink-0">2</span>
                <span>Scroll and tap <b>Add to Home Screen</b> <Plus size={14} className="inline mx-0.5 text-sky-600" />.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center shrink-0">3</span>
                <span>Tap <b>Add</b>. The DocSite icon will appear on your Home Screen — open it like any app.</span>
              </li>
            </ol>
            <button onClick={() => { setShowIosGuide(false); handleDismiss(); }} className="w-full mt-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-medium" data-testid="ios-install-guide-done">
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
